import type { BoardStatus, BoardWorkflow, WorkOrder } from '@/domain/work-order.types'

/** One rendered column: a configured status and the orders sitting in it. */
export interface BoardColumn {
	status: BoardStatus
	orders: WorkOrder[]
}

/** One rendered swimlane group: a workflow and its columns, in configured order. */
export interface BoardWorkflowGroup {
	workflowId: string
	name: string
	columns: BoardColumn[]
}

/**
 * What the board renders. `unconfigured` is deliberately part of the model
 * rather than a filtered-away remainder: a work order the configuration cannot
 * place must still appear somewhere, because a silently missing order is worse
 * than one flagged as needing attention.
 */
export interface BoardModel {
	groups: BoardWorkflowGroup[]
	unconfigured: WorkOrder[]
}

/**
 * Group work orders by workflow, then by configured status.
 *
 * Grouping is by workflow FIRST — two workflows may each define `in_progress`
 * with different labels and positions, and merging them by code alone would
 * silently fuse two unrelated columns. A work order lands in `unconfigured`
 * when it has no workflow, when its workflow is absent from the read model
 * (missing, inactive, soft-deleted, or another tenant's), or when its status is
 * not among that workflow's active statuses. No order is ever dropped for
 * holding a code the frontend does not recognise.
 */
export function buildBoardModel(orders: WorkOrder[], workflows: BoardWorkflow[]): BoardModel {
	const columnsByWorkflow = new Map<string, Map<string, BoardColumn>>(
		workflows.map((workflow) => [
			workflow.workflowId,
			new Map(
				[...workflow.statuses]
					.sort((a, b) => a.position - b.position || a.code.localeCompare(b.code))
					.map((status) => [status.code, { status, orders: [] as WorkOrder[] }]),
			),
		]),
	)
	const unconfigured: WorkOrder[] = []
	for (const order of orders) {
		const column = order.workflowId ? columnsByWorkflow.get(order.workflowId)?.get(order.status) : undefined
		if (column) column.orders.push(order)
		else unconfigured.push(order)
	}
	return {
		groups: workflows.map((workflow) => ({
			workflowId: workflow.workflowId,
			name: workflow.name,
			columns: [...(columnsByWorkflow.get(workflow.workflowId)?.values() ?? [])],
		})),
		unconfigured,
	}
}
